import { defineStore } from "pinia";
import { socket } from "@/user.socket";
import { joinJackpotHalls, bindJackpotTiersSocket } from "@/jp.socket";
import http, { setUserTimezone } from "@/composables/axios";
import { useResponseMessageStore } from "./response_message";
import { clearResourcesCache } from "./clearResources";
import { normalizeLang } from "@/helpers";
import { useI18n } from "vue-i18n";
export const useAuthStore = defineStore('auth', {
    state: () => ({
        token: undefined,
        user: undefined,
        currencies: [],
        credits: [],
        userLoaded: false,
        loginError: '',
        notices:[],
        marketTemplate:{},
        fixtureJackpots:[],
        jpTiers: [],
        jpTiersLoading: false
    }),

    getters: {
        isAuth: state => state.userLoaded && typeof state.user != 'undefined',
        isUserLoaded: state => state.userLoaded,
        getUser: state => state.user,
        getCredits: state => state.credits,
        getCurrencies: state => state.currencies,
        getCurrency: state => id => state.currencies.filter(c => c._id == id)[0],
        getLoginError: state => state.loginError,
        isMarketBlocked: state => id => state.user?.blcm.includes(Number(id)),
        isPreSportBlocked: state => id => (state.user?.blcsp ?? []).map(Number).includes(Number(id)),
        isLiveSportBlocked: state => id => (state.user?.blcsl ?? []).map(Number).includes(Number(id)),
        getNotices: state => state.notices,
        getMarketTemplate: state => state.marketTemplate,
        getFixtureJackpots: state => state.fixtureJackpots,
        getJpTiers: state => state.jpTiers,
        getJpTiersLoading: state => state.jpTiersLoading
    },

    actions: {
        async fetchUser() {
            const nav_lang = navigator.language || navigator.userLanguage
            if (localStorage.token) {
                await http.get('01/user').then((res) => {
                    if (res.data.ok) {
                        this.user = res.data.data.user
                        setUserTimezone(res.data.data.user?.tmz || '')
                        this.currencies = res.data.data.currencies
                        this.credits = res.data.data.credits
                        if(!localStorage.getItem('lang')){
                            localStorage.setItem('lang', normalizeLang(res.data.data.user?.def_lang || nav_lang))
                            window.location.reload()
                        }
                        if(res.data.data.user?.theme){
                            document.documentElement.setAttribute('th', res.data.data.user.theme)    
                        }
                        this.userLoaded = true
                    }
                }).catch(error => {
                    if (error.status == 404 && !error.response.data.ok) {
                        setUserTimezone('')
                        localStorage.removeItem('token')
                        window.location.reload()
                    }
                })
            }
        },
        async register(data){
            const responseMessageStore = useResponseMessageStore()
            await http.post('01/register', data).then(async res=>{
                if(res.data.ok){
                    await this.login({
                        username: data.username,
                        password: data.password
                    })
                }
            }).catch(error =>{
                responseMessageStore.setMessage(error.response.data.message)
            })
        },
        async login(credentials) {
            await http.post('01/login', credentials).then(async res => {
                if (res.data.ok) {
                    this.token = res.data.data
                    localStorage.setItem('token', res.data.data)
                    
                    await clearResourcesCache();
                    window.location.reload();
                }
            }).catch(e => {
                if (e.response.status == 401) {
                    this.loginError = 'login.unauthorized'
                } else if (e.response.status == 400) {
                    this.loginError = 'login.notAllowed'
                }
            })
        },

        async logout() {
            http.put('01/logout').then(async res => {
                if (res.status === 204) {
                    await clearResourcesCache();
                    setUserTimezone('');
                    localStorage.removeItem('token');
                    localStorage.removeItem('lang');
                    window.location.reload();
                }
            }).catch(err => {
                console.error('Logout failed', err);
            });
        },
        
        async acceptTerms() {
            const res = await http.put('01/users/terms');
            if (res.status === 204) {
              this.user.acc_term = true;
            }
            return res;
        },
        async updateDetails(payload) {
            return await http.put('01/user', payload)
        },

        async updatePassword(payload) {
            return await http.put('01/user/update-password', payload)
        },

        async updateSettings(payload) {
            return await http.put('01/users/settings', payload)
        },

        async fetchTransactions(query) {
            query = new URLSearchParams(query).toString()

            return await http.get(`01/user/transactions?${query}`)
        },
        async fetchJackpotTiers(){
            this.jpTiersLoading = true
            try {
                const res = await http.get(`01/user/jp-tiers`)
                if (res?.data?.ok && Array.isArray(res.data.data)) {
                    this.jpTiers = res.data.data
                }
                return res
            } finally {
                this.jpTiersLoading = false
            }
        },
        async fetchDefaultJackpotTiers(){
            this.jpTiersLoading = true
            try {
                const res = await http.get(`01/user/default-jp-tiers`)
                if (res?.data?.ok && res.data.data != null) {
                    this.jpTiers = Array.isArray(res.data.data) ? res.data.data : [res.data.data]
                }
                return res
            } finally {
                this.jpTiersLoading = false
            }
        }, 
        async fetchNotices(){
            const lang = normalizeLang(window.localStorage.getItem('lang'))
            return await http.get(`01/notices?lang=${lang}`).then((res) => {
                if (res.data.ok) {
                    this.notices = res.data.data
                }
            })
        },
        async fetchUserMarketTemplate (){
            return await http.get(`07/user/market-template`).then((res)=>{
                if(res.status == 200){
                    this.marketTemplate =res.data
                }
            })
        },
        async fetchFixtureJackpots(){
            return await http.get(`07/fixture-jackpots`).then((res)=>{
                if(res.status == 200){
                    this.fixtureJackpots = res.data
                }
            })
        },

        bindSocketEvents() {
            const responseMessageStore = useResponseMessageStore()

            socket.on('credit:updated', (data) => {
                if (data.ok) {
                    let index = this.credits.findIndex(c => c._id == data.data.creditId)

                    if (index > -1) {
                        this.credits[index].am = data.data.amount
                    }
                }
            })

            socket.on('ticket:on-wait', (data) => {
                if (data.ok) {
                    responseMessageStore.setMessage('Ticket is being confirmed...', false)
                }
            })

            const jackpotNames = this.jpTiers.map((jp) => jp.name)
            joinJackpotHalls(jackpotNames)
            bindJackpotTiersSocket(jackpotNames, (data) => {
                const updates = data?.data
                if (!updates || typeof updates !== 'object') return
                this.jpTiers = this.jpTiers.map((jp) => {
                    const newTiers = updates[jp.currency_id]
                    if (!Array.isArray(newTiers)) return jp
                    return { ...jp, tiers: newTiers }
                })
            })
        }
    }
})